import type { CameraConfig } from '../base/camera/type';
import type { LightingConfig } from '../base/light/type';
import type { RendererConfig } from '../base/renderer/type';
import type { LoaderConfig } from '../base/loader/type';

// 기본 로더 설정
export const defaultLoaderConfig: LoaderConfig = {
  options: {
    isKTX2: true,
    isDRACO: true,
    isMeshopt: true,
    isVRM: false,
  },
  paths: {
    ktx2: 'https://cdn.jsdelivr.net/npm/three@0.168.0/examples/jsm/libs/basis/',
    draco: 'https://cdn.jsdelivr.net/npm/three@0.168.0/examples/jsm/libs/draco/',
    meshopt: 'https://cdn.jsdelivr.net/npm/three@0.168.0/examples/jsm/libs/meshopt/'
  },
  supportedFormats: ['glb', 'gltf', 'fbx', 'vrm'] as const,
  progress: {
    enabled: true,
    throttle: 100,
  },
};

// 기본 카메라 설정
export const defaultCameraConfig: CameraConfig = {
  cameras: [
    {
      name: "main",
      type: "PerspectiveCamera",
      fov: 40,
      near: 0.1,
      far: 400,
      position: { x: 0, y: 28, z: 30.44 },
      rotation: { x: -9 * Math.PI / 180, y: 0, z: 0 * Math.PI / 180 },
      frustumCulled: true,
      active: true
    }
  ]
};

// 기본 조명 설정
export const defaultLightingConfig: LightingConfig = {
  environment: {
    loader: "EXR",
    path: "/hdr/env.exr"
  },
  lights: [
    {
      type: "DirectionalLight",
      color: 0xffffff,
      intensity: 1.3,
      position: { x: -0.953, y: 15.016, z: 12.993 },
      castShadow: true,
      frustumCulled: true,
      shadow: {
        autoUpdate: true,
        radius: 6,
        mapSize: { width: 1024, height: 1024 }
      }
    },
    {
      type: "DirectionalLight",
      color: 0xffecd6,
      intensity: 0.4,
      position: { x: 8.778, y: 1.735, z: 0.032 },
      castShadow: false,
      shadow: {
        radius: 1
      }
    },
    {
      type: "HemisphereLight",
      skyColor: 0xfff2db,
      groundColor: 0xd7d0d0,
      intensity: 1.5,
      position: { x: 0, y: 10, z: 0 },
      frustumCulled: true
    },
    {
      type: "PointLight",
      color: 0xffffff,
      intensity: 10,
      position: { x: 1.964, y: 8.688, z: 9.242 },
      frustumCulled: true,
      castShadow: false
    },
    {
      type: "AmbientLight",
      color: 0xffffff,
      intensity: 0.2
    }
  ]
};

// 기본 렌더러 설정
export const defaultRendererConfig: RendererConfig = {
  renderer: {
    antialias: true,
    powerPreference: "high-performance",
    alpha: true,
    premultipliedAlpha: false,
    precision: "highp",
    depth: true,
    stencil: false,
    logarithmicDepthBuffer: false,
    maxPixelRatio: 2,
    outputColorSpace: "srgb",
    toneMappingExposure: 1.0,
    shadow: {
      enabled: true,
      type: "PCFShadowMap",
      autoUpdate: true
    }
  },
  bloom: {
    enabled: true,
    config: {
      bloomParams: {
        strength: 0.5,
        radius: 0,
        threshold: 0.8,
        exposure: 1
      },
      blendParams: {
        blendOpacity: 0.1,
        bloomStrength: 1,
        applyGamma: false,
        blendMode: 'add'
      }
    }
  }
};

// 통합 기본 설정
export interface DefaultConfig {
  loader: LoaderConfig;
  camera: CameraConfig;
  lighting: LightingConfig;
  renderer: RendererConfig;
}

export const defaultConfig: DefaultConfig = {
  loader: defaultLoaderConfig,
  camera: defaultCameraConfig,
  lighting: defaultLightingConfig,
  renderer: defaultRendererConfig
}; 